import { useMemo } from 'react'
import type { MarkdownFeatures } from '@highlight/ui'

import type { MarkdownFeatureName, StagedActionContentMarkdownEdit } from './use-staged-actions'

/**
 * Map the backend's `MARKDOWN_FEATURES` enum onto the editor's flat
 * `MarkdownFeatures` shape. Anything not in the backend's `features`
 * array is disabled — the contract is "render only what the destination
 * supports", so unknown features get no UI.
 */
const FEATURE_MAP: Record<MarkdownFeatureName, ReadonlyArray<keyof MarkdownFeatures>> = {
  bold: ['bold'],
  italic: ['italic'],
  strikethrough: ['strike'],
  headings: ['headings'],
  bulletList: ['bulletList'],
  orderedList: ['orderedList'],
  checkbox: ['taskList'],
  link: ['link'],
  code: ['inlineCode'],
  codeBlock: ['codeBlock'],
  blockquote: ['blockquote'],
  table: ['table'],
  image: ['image'],
}

function featuresFromBackend(features: ReadonlyArray<MarkdownFeatureName>): MarkdownFeatures {
  const result: MarkdownFeatures = {
    headings: false,
    blockquote: false,
    bulletList: false,
    orderedList: false,
    taskList: false,
    codeBlock: false,
    horizontalRule: false,
    table: false,
    image: false,
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    highlight: false,
    inlineCode: false,
    link: false,
    textAlign: false,
    typography: false,
    mentions: false,
  }
  for (const name of features) {
    const keys = FEATURE_MAP[name]
    if (!keys) continue
    for (const key of keys) {
      // The mapped editor key is always a boolean toggle for the backend's
      // 13-feature surface — no headings-levels / image-config inputs flow
      // through this contract.
      ;(result as Record<string, unknown>)[key] = true
    }
  }
  return result
}

/**
 * Resolve the editor configuration from the backend-declared markdown edit
 * options. Returns `null` when content is not editable (the backend omits
 * `content.markdown` entirely), which the caller uses to hide the edit
 * affordance.
 */
export function useActionMarkdownConfig(
  markdown: StagedActionContentMarkdownEdit | undefined,
): { features: MarkdownFeatures; maxLength?: number } | null {
  return useMemo(() => {
    if (!markdown) return null
    return {
      features: featuresFromBackend(markdown.features),
      maxLength: markdown.constraints?.maxLength,
    }
  }, [markdown])
}
